import React, { useState, useEffect, useRef } from "react";
import "./Vim.css";

function Vim({ content, title, onClose }) {
  const [scrollIndex, setScrollIndex] = useState(0);
  const [linesToShow, setLinesToShow] = useState(0); // Number of lines to show
  const [lineHeight, setLineHeight] = useState(24); // Line height, could be dynamic
  const contentRef = useRef(null);
  const maxLineNumberLength = String(content.length).length;

  function renderLineContent(line) {
    const regex = /<a\s+href="([^"]+)"[^>]*>([^<]+)<\/a>|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(__([^_]+)__)/g;  
    const parts = [];
    let lastIndex = 0;
    let match;
  
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
  
      if (match[1] && match[2]) {
        parts.push(
          <a href={match[1]} target="_blank" rel="noopener noreferrer" key={match[1]}>
            {match[2]}
          </a>
        );
      } else if (match[3] && match[4]) {
        parts.push(<strong key={`bold-${match.index}`}>{match[4]}</strong>);
      } else if (match[5] && match[6]) {
        parts.push(<em key={`italic-${match.index}`}>{match[6]}</em>);
      } else if (match[7] && match[8]) {
        parts.push(<u key={`underline-${match.index}`}>{match[8]}</u>);
      }
      lastIndex = regex.lastIndex;
    }  
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    return parts;
  };  

  const findNumLines = (topLineIdx, windowHeight, windowWidth, charWidth, charHeight) => {
    let lines = 0;
    let dispPixelsAvail = windowHeight - charHeight;
    let partialFitUsed = false;
    windowWidth -= 66; // TODO: CHECK
  
    while (dispPixelsAvail > charHeight && topLineIdx + lines < content.length) {
      const text = content[topLineIdx + lines];
      const numLinesFilled = Math.max(Math.ceil(text.length / Math.floor(windowWidth / charWidth)), 1);
      const numPixelsFilled = numLinesFilled * (charHeight) + 8;
      if (dispPixelsAvail >= numPixelsFilled) {
        dispPixelsAvail -= numPixelsFilled; 
        lines++;
      } else {
        partialFitUsed = true;
        dispPixelsAvail = 0;
      }
    }
    if (partialFitUsed && lines < content.length - topLineIdx) {
      lines++;
    }
    return lines;
    };

  useEffect(() => {
    const calculateLinesToShow = () => {
      if (contentRef.current) {
        const windowHeight = contentRef.current.offsetHeight;
        const windowWidth = contentRef.current.offsetWidth;
        const charHeight = lineHeight;
        const charWidth = charHeight * 0.6;
        const linesToDisplay = findNumLines(scrollIndex, windowHeight, windowWidth, charWidth, charHeight);
        setLinesToShow(linesToDisplay);
      }
    };
    calculateLinesToShow();
    window.addEventListener("resize", calculateLinesToShow);
    return () => window.removeEventListener("resize", calculateLinesToShow);
  }, [content.length, lineHeight, scrollIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown" && !e.shiftKey) {
        setScrollIndex((prev) =>
          Math.min(prev + 1, Math.max(content.length - linesToShow, 0))
        );
      } else if (e.key === "ArrowUp" && !e.shiftKey) {
        setScrollIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "ArrowDown" && e.shiftKey) {
        setScrollIndex((prev) =>
          Math.min(prev + linesToShow, Math.max(content.length - linesToShow, 0))
        );
      } else if (e.key === "ArrowUp" && e.shiftKey) {
        setScrollIndex((prev) => Math.max(prev - linesToShow, 0));
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [content.length, linesToShow, onClose]);

  return (
    <div className="vim-container">
      <div className="vim-header">
        <p>{title}</p>
      </div>
      <div className="vim-content" ref={contentRef}>
        {content.slice(scrollIndex, scrollIndex + linesToShow).map((line, index) => (
          <div key={index} className="vim-line">
            <span className="line-number">
              {(scrollIndex + index + 1).toString().padEnd(maxLineNumberLength, " ")}
            </span>{" "}
            <p>{renderLineContent(line)}</p>
          </div>
        ))}
      </div>
      <div className="vim-footer">
        <p>ESC: quit</p>
        <p>
          {'\u25B2'}{'\u25BC'}: scroll up/down
        </p>
        <p>
          shift + {'\u25B2'}{'\u25BC'}: page up/down
        </p>
        <p>
          Lines {(scrollIndex + 1).toString().padStart(maxLineNumberLength, "0")}-{Math.min(scrollIndex + linesToShow, content.length).toString().padStart(maxLineNumberLength, "0")} / {content.length}
        </p>
      </div>
    </div>
  );
}

export default Vim;